import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getUser } from '@/services/coloc-api/auth';
import { isUserAuthenticated } from '@/utils/server-state';

const useAuth = () => {
  const userQuery = useQuery({
    queryKey: ['getUser'],
    queryFn: () => getUser(),
    enabled: isUserAuthenticated(),
    staleTime: 5 * 60 * 1000,
  });

  const user = useMemo(() => {
    if (userQuery.data?.data.data.attributes) {
      return userQuery.data?.data.data.attributes;
    }

    return undefined;
  }, [userQuery.data]);

  const roleChecks = useMemo(() => {
    return {
      isSuperAdmin: user?.type === 'AdminUser' && user?.role === 'superadmin',
      isPropertyManager: user?.type === 'AdminUser' && user?.role === 'property_manager',
      isRentsAndDepositManager: user?.type === 'AdminUser' && user?.role === 'rents_and_deposit_manager'
    };
  }, [user]);


  return {
    user,
    userFetchingStatus: userQuery.status,
    fetchUser: userQuery.refetch,
    ...roleChecks,
  };
};

const AuthContext = createContext<ReturnType<typeof useAuth> | undefined>(
  undefined
);

const AuthContextProvider = ({ children }: { children: React.ReactNode }) => {
  const hook = useAuth();

  return <AuthContext.Provider value={hook}>{children}</AuthContext.Provider>;
};

const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuthContext must be used within a AuthContextProvider');
  }

  return context;
};

export { useAuthContext, AuthContextProvider };
